/**
 * Safely read the current TanStack Router instance + state, degrading
 * gracefully when there is NO `RouterProvider` in the tree.
 *
 * RR3's `<Link>` and `withRouter` worked even when rendered without a router
 * context (common in component tests using a plain RTL render). TanStack's
 * `useRouterState`/`useParams` THROW in that case, so instead we read the router
 * via `useRouter({warn: false})` (which returns `null` rather than throwing) and
 * subscribe to its state through `useSyncExternalStore` for reactivity. When no
 * router is present, consumers fall back to the global `routerHistory`.
 */
import {useCallback, useSyncExternalStore} from 'react';
import {useRouter, type AnyRouter, type RouterState} from '@tanstack/react-router';

export interface RouterSnapshot {
  router: AnyRouter | null;
  state: RouterState | null;
}

export function useRouterSnapshot(): RouterSnapshot {
  const router = (useRouter({warn: false}) as AnyRouter | null) ?? null;

  const subscribe = useCallback(
    (onChange: () => void) => (router ? router.subscribe('onResolved', onChange) : () => undefined),
    [router]
  );
  const getSnapshot = useCallback(() => (router ? router.state : null), [router]);

  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {router, state};
}
